import React from 'react';
import {useParams} from 'react-router';
import {
  useGetActivitiesQuery,
  useGetActivitiesDownloadMutation,
  useGetProfileActivitiesQuery
} from '../../store/apis/ccs';
import RtkQueryFeedback from '@components/common/RTKQueryFeedback';
import {Box, Button, Card, CardContent, CardHeader, Divider, Grid2, Typography} from '@mui/material';
import {LabeledField, styles} from '@invoice-system';
import {t} from 'ttag';
import {Download} from '@mui/icons-material';
import {DataGrid} from '@mui/x-data-grid';
import {createDatetime, DATE_FORMAT} from '../../utils/datetime/datetime-utils';


export const ActivitiesList = () => {
  const params = useParams();
  const {username, startDate, endDate} = params;
  const {data, error, isLoading} = useGetActivitiesQuery(
    {
      username: username as string,
      startDate: startDate as string,
      endDate: endDate as string
    },
    {skip: !username || !startDate || !endDate}
  );
  const {
    data: profile,
    isLoading: isLoadingProfile
  } = useGetProfileActivitiesQuery({username: username as string}, {skip: !username});
  const [onDownloadPdf, {isLoading: loadingPdf, error: downloadError}] = useGetActivitiesDownloadMutation();


  const handleDownloadPdf = ({username, startDate, endDate}: {username: string; startDate: string; endDate: string}) => {
    onDownloadPdf({username, startDate, endDate})
      .unwrap()
      .then((base64: string) => {
        // Decode base64 to binary
        const binaryString = atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `activities_${username}_${startDate}_${endDate}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error('Download failed:', error);
      });
  };

  const totals = {
    tierPoints: Array.isArray(data) ? data.reduce((sum, row) => sum + (Number(row.tierPoints) || 0), 0) : 0,
    awardPoints: Array.isArray(data) ? data.reduce((sum, row) => sum + (Number(row.awardPoints) || 0), 0) : 0,
    redeemedPoints: Array.isArray(data) ? data.reduce((sum, row) => sum + (Number(row.redeemedPoints) || 0), 0) : 0,
    redepositedPoints: Array.isArray(data) ? data.reduce((sum, row) => sum + (Number(row.redepositedPoints) || 0), 0) : 0,
    flightSectors: Array.isArray(data) ? data.reduce((sum, row) => sum + (Number(row.flightSectors) || 0), 0) : 0,
  };

  return (
    <>
      <Card elevation={3}>
        <CardHeader
          title={
            <Typography
              sx={styles.title}
            >{t`Client ${username} Activities`}</Typography>
          }
          subheader={
            <Typography sx={styles.subHeader} variant={'subtitle2'}>
              {t`Activities between ${startDate} and ${endDate}`}
            </Typography>
          }
          action={
            <Box display={'flex'} my={'auto'}>
              <Button
                variant={'contained'}
                color={'primary'}
                sx={{fontFamily: 'OpenSansMedium'}}
                onClick={() => handleDownloadPdf({
                  username: username || '',
                  startDate: startDate || '',
                  endDate: endDate || ''
                })}
                startIcon={<Download />}
              >
                {t`Export Activities PDF`}
              </Button>
            </Box>
          }
        />
        <Divider />
        <Grid2 container spacing={2} sx={{p: 2}}>
          <Grid2 size={{xs: 12, sm: 6}}>
            <LabeledField label={t`First Name`} value={profile?.firstName} />
          </Grid2>
          <Grid2 size={{xs: 12, sm: 6}}>
            <LabeledField label={t`Surname`} value={profile?.lastName} />
          </Grid2>
          <Grid2 size={{xs: 12, sm: 6}}>
            <LabeledField label={t`Membership Number`} value={profile?.username} />
          </Grid2>
          <Grid2 size={{xs: 12, sm: 6}}>
            <LabeledField label={t`Card Tier`} value={profile?.loyalty?.cardType} />
          </Grid2>
          <Grid2 size={{xs: 12, sm: 6}}>
            <LabeledField label={t`Status Miles`} value={profile?.loyalty?.statusMiles?.toString()} />
          </Grid2>
          <Grid2 size={{xs: 12, sm: 6}}>
            <LabeledField label={t`Flight Sectors`} value={profile?.loyalty?.statusSegments?.toString()} />
          </Grid2>
          <Grid2 size={{xs: 12, sm: 6}}>
            <LabeledField label={t`Award Miles Balance`} value={profile?.loyalty?.miles?.toString()} />
          </Grid2>
          <Grid2 size={{xs: 12, sm: 6}}>
            <LabeledField
              label={t`Address`}
              value={[
                profile?.addresses?.[0]?.address,
                profile?.addresses?.[0]?.poBox && `PO Box: ${profile.addresses[0].poBox}`,
                profile?.addresses?.[0]?.city,
                profile?.addresses?.[0]?.state,
                profile?.addresses?.[0]?.zipCode,
                profile?.addresses?.[0]?.country
              ]
                .filter(Boolean)
                .join(', ') || t`No address available`}
            />
          </Grid2>
        </Grid2>
        <CardContent sx={{py: 4}}>
          <DataGrid
            rows={data || []}
            columns={[
              {field: 'activityDate', headerName: t`Activity Date`, flex: 1, disableColumnMenu: true, sortable: false, valueFormatter: (params) => params?.value ? createDatetime(params?.value)?.toFormat(DATE_FORMAT) : ''},
              {field: 'definition', headerName: t`Description`, flex: 2, disableColumnMenu: true, sortable: false},
              {field: 'tierPoints', headerName: t`Status Miles`, flex: 1, disableColumnMenu: true, sortable: false},
              {field: 'awardPoints', headerName: t`Award Points`, flex: 1, disableColumnMenu: true, sortable: false},
              {field: 'redeemedPoints', headerName: t`Earned Miles`, flex: 1, disableColumnMenu: true, sortable: false},
              {field: 'redepositedPoints', headerName: t`Redeemed Miles`, flex: 1, disableColumnMenu: true, sortable: false},
              {field: 'flightSectors', headerName: t`Status Sectors`, flex: 1, disableColumnMenu: true, sortable: false},

              // {field: 'company', headerName: t`Company`, width: 200, disableColumnMenu: true},
              // {field: 'expireDate', headerName: t`Expire Date`, width: 150, disableColumnMenu: true},
              // {field: 'membershipNumber', headerName: t`Membership Number`, width: 150, disableColumnMenu: true},
              // {field: 'statementDate', headerName: t`Statement Date`, width: 150, disableColumnMenu: true},
              // {field: 'status', headerName: t`Status`, width: 100, disableColumnMenu: true},
              // {field: 'transactionDate', headerName: t`Transaction Date`, width: 150, disableColumnMenu: true}
            ]}
            hideFooter
            autoHeight
            getRowId={(row) => row.id}

            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                fontWeight: 'bold'
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer'
              }
            }}
          />
          <Box
            display="flex"
            alignItems="center"
            sx={{
              backgroundColor: '#f5f5f5',
              fontWeight: 'bold',
              borderTop: '1px solid #e0e0e0',
              px: 2,
              py: 1
            }}
          >
            <Box flex={1}>{t`Total`}</Box>
            <Box flex={2} />
            <Box flex={1}>{totals.tierPoints}</Box>
            <Box flex={1}>{totals.awardPoints}</Box>
            <Box flex={1}>{totals.redeemedPoints}</Box>
            <Box flex={1}>{totals.redepositedPoints}</Box>
            <Box flex={1}>{totals.flightSectors}</Box>
          </Box>
        </CardContent>
        <Divider />
      </Card>
      <RtkQueryFeedback isLoading={isLoading || loadingPdf || isLoadingProfile} error={error || downloadError} />
    </>
  );
};

export default ActivitiesList;
